/**
 * sessionTranscript — the words of a session, in one place.
 *
 * A session's transcript arrives two ways today (Brief K §2a): pasted as a
 * session_artifacts row of type 'ai_transcript' (the Documents tab), or
 * recorded in the browser as prioritization_session_transcript_segments
 * (migration 489, the Transcript tab). Both the prioritization memo and the
 * transcript item linker read it here, so they always read the same words.
 *
 * Recorded segments are written one per line with their start time, as
 * "[12:03] Speaker: words", so a passage found in the text carries the moment
 * it was said.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { isSupabaseAvailable } from './supabaseSync';

/** A segment's offset from the start of the recording: "12:03", or "1:02:09" past an hour. */
export function formatOffsetClock(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export interface SessionTranscript {
  /** Every pasted transcript, then the recorded segments; '' when the session has none. */
  text: string;
  pastedCount: number;
  segmentCount: number;
}

export interface TranscriptSourceRows {
  pasted: Array<{ title: string | null; content: string | null }>;
  segments: Array<{ text: string | null; speaker_label: string | null; start_ms: number | null }>;
}

/** Pure: the text the memo and the linker read, from the rows. */
export function composeSessionTranscript(rows: TranscriptSourceRows): SessionTranscript {
  const pasted = rows.pasted
    .filter((a) => typeof a.content === 'string' && a.content.trim())
    .map((a) => `[${a.title?.trim() || 'Transcript'}]\n${(a.content as string).trim()}`);
  const lines = rows.segments
    .filter((s) => typeof s.text === 'string' && s.text.trim())
    .map((s) => {
      const at = typeof s.start_ms === 'number' ? `[${formatOffsetClock(s.start_ms)}] ` : '';
      return `${at}${s.speaker_label ? `${s.speaker_label}: ` : ''}${(s.text as string).trim()}`;
    });
  const text = [...pasted, lines.length ? `[Recorded in the session]\n${lines.join('\n')}` : '']
    .filter(Boolean)
    .join('\n\n');
  return { text, pastedCount: pasted.length, segmentCount: lines.length };
}

/** The session's transcript as one text. Never throws; a read that fails reads as no transcript. */
export async function loadSessionTranscript(sessionId: string): Promise<SessionTranscript> {
  if (!isSupabaseAvailable()) return { text: '', pastedCount: 0, segmentCount: 0 };
  const db = getTypedSupabaseClient();
  const [artifacts, segments] = await Promise.all([
    db.from('session_artifacts').select('title, content').eq('session_id', sessionId).eq('artifact_type', 'ai_transcript').order('created_at'),
    db.from('prioritization_session_transcript_segments').select('text, speaker_label, start_ms').eq('session_id', sessionId).order('start_ms').limit(2000),
  ]);
  return composeSessionTranscript({ pasted: artifacts.data ?? [], segments: segments.data ?? [] });
}
